import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom';
import { useUserContext } from '../../context/users/UserContext'
import { 
  FaQuestionCircle, 
  FaPlus, 
  FaSearch, 
  FaPen, 
  FaTrash,
  FaExclamationTriangle,
  FaTimes
} from 'react-icons/fa';
import { Card, Row, Col, Form, Button, Table, Alert, Modal } from 'react-bootstrap'
import {
  getQuestions,
  removeQuestion
} from "../../context/questions/QuestionActions";
import { getDescriptionOptions } from '../../utils/questionDescriptions'
import './QuestionsList.css'

// eslint-disable-next-line
export default function QuestionsList() {
  const navigate = useNavigate()
  const { authenticatedUser, setAlert } = useUserContext()

  const [questions, setQuestions] = useState([])
  const [allQuestions, setAllQuestions] = useState([]) // Para almacenar todas las preguntas
  const [selectedOption, setSelectedOption] = useState(null); // Cambiar a null para mostrar "Todas"
  const [keywords, setKeywords] = useState('')
  const [pag, setPag] = useState(1);
  const [maxNextPage, setMaxNextPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false)
  
  // Estados para el modal de confirmación
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [questionToDelete, setQuestionToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  window.scrollTo({
    top: 0,
    behavior: 'auto',
  })

  const fetchData = async () => {
    setIsLoading(true)
    try {
      // Cargar todas las preguntas sin paginación para el filtrado
      const response = await getQuestions({ 
        path: 'questions', 
        token: authenticatedUser.value, 
        page: 1, 
        pageSize: 1000 // Cargar muchas preguntas para tener todas disponibles
      });

      if (response.error) {
        setQuestions([]);
        setAllQuestions([]);
        setAlert({
          message: response.message || 'Error al cargar las preguntas',
          msgType: 'warning'
        })
      } else {
        setAllQuestions(response.data);
        setQuestions(response.data); // Mostrar todas inicialmente
        
        const count = response.paging.totalRecords;
        if (count > 0) {
          let number = Math.ceil(count / 10);
          setMaxNextPage(number);
        }
      }
    } catch (error) {
      setAlert({
        message: 'Error al cargar las preguntas',
        msgType: 'warning'
      })
      setQuestions([])
      setAllQuestions([])
    } finally {
      setIsLoading(false)
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSelectChange = (selectedOption) => {
    setSelectedOption(selectedOption)
    
    // Filtrar preguntas basado en la selección
    if (selectedOption && selectedOption.value) {
      const filteredQuestions = allQuestions.filter(question => question.description === selectedOption.value);
      setQuestions(filteredQuestions);
    } else {
      // Si no hay selección, mostrar todas las preguntas
      setQuestions(allQuestions);
    }
  };

  const handleChangeKeywords = (e) => {
    setKeywords(e.target.value)
  }

  // Función para normalizar texto (quitar tildes y acentos)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  // Filtrar preguntas por keywords
  useEffect(() => {
    if (keywords.trim() === '') {
      // Si no hay keywords, aplicar solo el filtro de descripción
      if (selectedOption && selectedOption.value) {
        const filteredQuestions = allQuestions.filter(question => question.description === selectedOption.value);
        setQuestions(filteredQuestions);
      } else {
        setQuestions(allQuestions);
      }
    } else {
      // Filtrar por keywords y descripción (ignorando tildes)
      const normalizedKeywords = normalizeText(keywords);
      let filteredQuestions = allQuestions.filter(question => 
        normalizeText(question.question).includes(normalizedKeywords) ||
        normalizeText(question.description).includes(normalizedKeywords)
      );
      
      if (selectedOption && selectedOption.value) {
        filteredQuestions = filteredQuestions.filter(question => question.description === selectedOption.value);
      }
      
      setQuestions(filteredQuestions);
    }
  }, [keywords, selectedOption, allQuestions]);


  const handleQuestionDelete = (question) => {
    if (question.hasSessions) {
      setAlert({
        message: 'Esta pregunta no se puede eliminar porque tiene registros asociados',
        msgType: 'warning'
      })
      return
    }
    
    setQuestionToDelete(question)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!questionToDelete) return
    
    setIsDeleting(true)
    
    try {
      let response = await removeQuestion({ 
        path: 'questions',
        id: questionToDelete.questionId,
        data: {
          deleted: true,
        },
        token: authenticatedUser.value
      })

      if (response) {
        // Actualizar tanto questions como allQuestions
        const updatedQuestions = questions.filter((question) => question.questionId !== questionToDelete.questionId)
        const updatedAllQuestions = allQuestions.filter((question) => question.questionId !== questionToDelete.questionId)
        
        setQuestions(updatedQuestions)
        setAllQuestions(updatedAllQuestions)
        
        // Si el filtro actual ya no tiene preguntas, limpiar la selección
        if (selectedOption && selectedOption.value) {
          const hasQuestionsWithDescription = updatedAllQuestions.some(q => q.description === selectedOption.value)
          if (!hasQuestionsWithDescription) {
            setSelectedOption(null)
          }
        }
        
        setAlert({
          message: `Pregunta eliminada exitosamente`,
          msgType: 'success'
        })
        
        // Cerrar modal y limpiar estado
        setShowDeleteModal(false)
        setQuestionToDelete(null)
      }
    } catch (error) {
      setAlert({
        message: 'Error al eliminar la pregunta',
        msgType: 'warning'
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setQuestionToDelete(null)
  }

  // Ya no necesitamos esta variable, usamos getSelectOptions()

  return (
    <div className="questions-container">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaQuestionCircle />
            </div>
            <div className="header-text">
              <h1>Gestión de Preguntas</h1>
              <p>Administra y gestiona las preguntas del sistema de evaluación</p>
            </div>
          </div>
        </div>

        {/* Actions Card */}
        <Card className="actions-card">
          <Card.Body>
            <Row className="align-items-center">
              <Col lg={4} md={12} sm={12} className="mb-3 mb-lg-0">
                <Button 
                  variant="primary" 
                  className="add-question-btn"
                  onClick={() => navigate('/preguntas/agregar')}
                >
                  <FaPlus />
                  Agregar Pregunta
                </Button>
              </Col>
              <Col lg={4} md={6} sm={12} className="mb-3 mb-lg-0">
                <div className="search-input-container">
                  <FaSearch className="search-icon" />
                  <Form.Control
                    type="text"
                    placeholder="Buscar preguntas por texto o descripción..."
                    value={keywords}
                    onChange={handleChangeKeywords}
                    className="search-input"
                  />
                </div>
              </Col>
              <Col lg={4} md={6} sm={12}>
                <div className="filter-container">
                  <select 
                    className="form-select filter-select"
                    value={selectedOption ? selectedOption.value : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const option = value ? { label: value, value: value } : null;
                      handleSelectChange(option);
                    }}
                  >
                    <option value="">Todas las descripciones</option>
                    {[...new Set(allQuestions.map(q => q.description))].map(desc => (
                      <option key={desc} value={desc}>
                        {desc}
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Questions Table Card */}
        <Card className="table-card">
          <Card.Body>
            {isLoading ? (
              <div className="loading-container">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <p className="mt-3">Cargando preguntas...</p>
              </div>
            ) : questions.length === 0 ? (
              <div className="empty-state">
                <FaQuestionCircle className="empty-icon" />
                <h4>No se encontraron preguntas</h4>
                <p>Intenta con otros términos de búsqueda o agrega una nueva pregunta.</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="table-responsive d-none d-lg-block">
                  <Table className="questions-table">
                    <thead>
                      <tr>
                        <th>Id</th>
                        <th>Pregunta</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((question) => (
                        <tr key={question.questionId} className="question-row">
                          <td className="question-id">#{question.questionId}</td>
                          <td className="question-text">{question.question}</td>
                          <td className="question-description">{question.description}</td>
                          <td>
                            <div className="d-flex gap-2 justify-content-end">
                              <Link to={`/preguntas/${question.questionId}/actualizar`}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="edit-btn"
                                  title="Editar pregunta"
                                >
                                  <FaPen />
                                </Button>
                              </Link>
                              <button
                                onClick={() => handleQuestionDelete(question)}
                                className="delete-btn"
                                title="Eliminar pregunta"
                                disabled={question.hasSessions}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Tablet Table View - Hide ID column */}
                <div className="table-responsive d-none d-md-block d-lg-none">
                  <Table className="questions-table">
                    <thead>
                      <tr>
                        <th>Pregunta</th>
                        <th>Descripción</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.map((question) => (
                        <tr key={question.questionId} className="question-row">
                          <td className="question-text">{question.question}</td>
                          <td className="question-description">{question.description}</td>
                          <td>
                            <div className="d-flex gap-2 justify-content-end">
                              <Link to={`/preguntas/${question.questionId}/actualizar`}>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="edit-btn"
                                  title="Editar pregunta"
                                >
                                  <FaPen />
                                </Button>
                              </Link>
                              <button
                                onClick={() => handleQuestionDelete(question)}
                                className="delete-btn"
                                title="Eliminar pregunta"
                                disabled={question.hasSessions}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Mobile Cards View - PrimeReact Style */}
                <div className="questions-cards d-lg-none">
                  {questions.map((question, index) => (
                    <Card key={question.questionId} className="question-card">
                      <Card.Body className="question-card-body">
                        <div className="question-card-content">
                          <div className="question-avatar">
                            <FaQuestionCircle />
                          </div>
                          <div className="question-info">
                            <h6 className="question-name">
                              <Link 
                                to={`/preguntas/${question.questionId}/actualizar`}
                                className="question-name-link"
                              >
                                {question.question}
                              </Link>
                            </h6>
                            <p className="question-description-mobile">{question.description}</p>
                          </div>
                          <div className="question-actions">
                            <Link to={`/preguntas/${question.questionId}/actualizar`}>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                className="edit-btn"
                                title="Editar pregunta"
                              >
                                <FaPen />
                              </Button>
                            </Link>
                            <button
                              onClick={() => handleQuestionDelete(question)}
                              className="delete-btn"
                              title="Eliminar pregunta"
                              disabled={question.hasSessions}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  ))}
                </div>

                {/* Mostrar contador de resultados */}
                {questions.length > 0 && (
                  <div className="results-info mt-3 text-center">
                    <p className="text-muted">
                      Mostrando {questions.length} pregunta{questions.length !== 1 ? 's' : ''}
                      {selectedOption && selectedOption.value && (
                        <span> de la descripción: <strong>{selectedOption.label}</strong></span>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      {/* Modal de Confirmación de Eliminación */}
      <Modal 
        show={showDeleteModal} 
        onHide={cancelDelete}
        centered
        backdrop="static"
        keyboard={false}
        className="delete-confirm-modal"
      >
        <Modal.Header className="delete-modal-header">
          <div className="modal-icon-container">
            <FaExclamationTriangle className="modal-warning-icon" />
          </div>
          <div className="modal-title-container">
            <Modal.Title className="modal-title">Confirmar Eliminación</Modal.Title>
            <p className="modal-subtitle">Esta acción no se puede deshacer</p>
          </div>
          <Button 
            variant="link" 
            onClick={cancelDelete}
            className="modal-close-btn"
            disabled={isDeleting}
          >
            <FaTimes />
          </Button>
        </Modal.Header>
        
        <Modal.Body className="delete-modal-body">
          <div className="question-preview">
            <h6 className="preview-label">Pregunta a eliminar:</h6>
            <div className="question-content">
              <p className="question-text">
                <strong>ID:</strong> #{questionToDelete?.questionId}
              </p>
              <p className="question-text">
                <strong>Pregunta:</strong> {questionToDelete?.question}
              </p>
              <p className="question-text">
                <strong>Descripción:</strong> {questionToDelete?.description}
              </p>
            </div>
          </div>
          
          <div className="warning-message">
            <FaExclamationTriangle className="warning-icon" />
            <p>¿Estás seguro de que quieres eliminar esta pregunta? Esta acción es permanente y no se puede deshacer.</p>
          </div>
        </Modal.Body>
        
        <Modal.Footer className="delete-modal-footer">
          <Button 
            variant="outline-secondary" 
            onClick={cancelDelete}
            disabled={isDeleting}
            className="cancel-delete-btn"
          >
            Cancelar
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={isDeleting}
            className="confirm-delete-btn"
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Eliminando...
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                Eliminar Pregunta
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
